import { findBand, BAND_COLORS } from "../bands";

// DAS28 — Disease Activity Score 28 (rheumatoid arthritis), ESR or CRP variant.
// ⚠️ Formula constants verified against the published DAS28 definitions; keep a
// clinician in the loop before relying on these clinically.
const das28Bands = [
    { label: "Remission", color: BAND_COLORS.remission, lt: 2.6 },
    { label: "Low", color: BAND_COLORS.low, lte: 3.2 },
    { label: "Moderate", color: BAND_COLORS.moderate, lte: 5.1 },
    { label: "High", color: BAND_COLORS.high },
];

export const das28 = {
    id: "das28",
    name: "DAS28",
    subtitle: "Disease activity score 28 (rheumatoid arthritis)",
    group: "Rheumatoid arthritis",
    status: "ready",
    options: [
        { key: "type", label: "Acute-phase reactant", choices: ["ESR", "CRP"], default: "ESR" },
    ],
    fields: [
        { key: "tjc28", label: "Tender joint count (0–28)", unit: "joints", type: "number", min: 0, max: 28 },
        { key: "sjc28", label: "Swollen joint count (0–28)", unit: "joints", type: "number", min: 0, max: 28 },
        {
            key: "gh",
            label: "Patient general health (VAS)",
            unit: "cm",
            type: "number",
            min: 0,
            max: 10,
            step: 0.1,
        },
        {
            key: "marker",
            label: (o) => (o.type === "CRP" ? "CRP" : "ESR"),
            unit: (o) => (o.type === "CRP" ? "mg/L" : "mm/h"),
            type: "number",
            min: (o) => (o.type === "CRP" ? 2 : 1),
            max: (o) => (o.type === "CRP" ? 500 : 200),
            step: 0.1,
        },
    ],
    compute: (v, o) => {
        const { tjc28, sjc28, marker, gh } = v;
        // gh is a 0–10 cm VAS; the 0.014 coefficient expects a 0–100 mm scale,
        // so multiply by 10 (0.014 * gh*10 == 0.14 * gh).
        const base = 0.56 * Math.sqrt(tjc28) + 0.28 * Math.sqrt(sjc28) + 0.14 * gh;
        if (o.type === "CRP") {
            return base + 0.36 * Math.log(marker + 1) + 0.96;
        }
        // ESR variant needs ln(ESR); ESR of 0 is not computable.
        if (marker <= 0) return null;
        return base + 0.7 * Math.log(marker);
    },
    interpret: (score) => findBand(score, das28Bands),
    bands: () => das28Bands,
    about:
        "Disease activity score 28 (DAS28) is a validated measure of disease activity in rheumatoid arthritis, derived from 4 variables: number of tender joints (0–28), number of swollen joints (0–28), patient general health on a 0–10 cm visual analogue scale, and an acute-phase reactant (ESR or CRP). The '28' version is a simplification of the original DAS, which requires 44 joints to be counted. The ESR and CRP variants are not interchangeable and can give different values for the same patient.",
    references: [
        "Prevoo ML, et al. Modified disease activity scores that include twenty-eight-joint counts. Arthritis Rheum. 1995.",
        "Wells G, et al. Validation of the 28-joint DAS using CRP. Ann Rheum Dis. 2009.",
    ],
};
